import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QrImage } from './QrImage';
import { formatMoney } from '../lib/format';
import { buildPixPayload, buildSepaPayload } from '../lib/payments';

export interface PaymentConfig {
  currency: string;
  locale: string;
  pix_key: string | null;
  pix_merchant_name: string | null;
  pix_city: string | null;
  sepa_iban: string | null;
  sepa_name: string | null;
}

export function PaymentQr({
  amountCents,
  reference,
  cfg
}: {
  amountCents: number;
  reference: string;
  cfg: PaymentConfig;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const result = useMemo<{ payload?: string; error?: 'not_configured' | 'unsupported'; sepa?: boolean }>(() => {
    if (cfg.currency === 'BRL') {
      if (!cfg.pix_key) return { error: 'not_configured' };
      return {
        payload: buildPixPayload({
          key: cfg.pix_key,
          merchantName: cfg.pix_merchant_name ?? '',
          city: cfg.pix_city ?? '',
          amountCents,
          reference
        })
      };
    }
    if (cfg.currency === 'EUR') {
      if (!cfg.sepa_iban) return { error: 'not_configured' };
      return {
        payload: buildSepaPayload({
          iban: cfg.sepa_iban,
          name: cfg.sepa_name ?? '',
          amountCents,
          remittance: reference
        }),
        sepa: true
      };
    }
    return { error: 'unsupported' };
  }, [cfg, amountCents, reference]);

  if (result.error) {
    return <p className="muted-note">{t(`pay.${result.error}`)}</p>;
  }

  async function copy() {
    if (!result.payload) return;
    await navigator.clipboard.writeText(result.payload);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="pay-qr">
      <div className="pay-qr__amount">{formatMoney(amountCents, cfg.currency, cfg.locale)}</div>
      <QrImage value={result.payload!} size={200} />
      <p className="muted-note">{t('pay.scan')}</p>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => void copy()}>
        {copied ? t('pay.copied') : t('pay.copy')}
      </button>
      {result.sepa && <p className="pay-qr__note">{t('pay.sepa_note')}</p>}
    </div>
  );
}
